import * as React from 'react'
import { useMemo } from 'react'
import { Button, Divider, Menu, MenuItemRadio, MenuList, MenuPopover, MenuTrigger, mergeClasses, Tooltip } from '@fluentui/react-components'
import { CalendarDayRegular, CalendarRegular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'
import { BaseIntervalOverviewFilterType, generateFromDate, generateToDate } from './BaseOverviewFilter'
import { BaseOverviewProps, useOverviewStyles } from './BaseOverview'
import { EntityDto } from '../../config/appConfig'

export type BaseIntervalOverviewFilterProps = BaseOverviewProps<EntityDto, BaseIntervalOverviewFilterType> & {
    filterItems?: BaseIntervalOverviewFilterType[]
}

export const filterTagDayItems: BaseIntervalOverviewFilterType[] = [
    {
        from: generateFromDate('monday'),
        to: generateToDate('monday'),
        tag: 'monday',
        icon: <CalendarDayRegular />,
    },
    {
        from: generateFromDate('tuesday'),
        to: generateToDate('tuesday'),
        tag: 'tuesday',
        icon: <CalendarDayRegular />,
    },
    {
        from: generateFromDate('wednesday'),
        to: generateToDate('wednesday'),
        tag: 'wednesday',
        icon: <CalendarDayRegular />,
    },
    {
        from: generateFromDate('thursday'),
        to: generateToDate('thursday'),
        tag: 'thursday',
        icon: <CalendarDayRegular />,
    },
    {
        from: generateFromDate('friday'),
        to: generateToDate('friday'),
        tag: 'friday',
        icon: <CalendarDayRegular />,
    },
    {
        from: generateFromDate('saturday'),
        to: generateToDate('saturday'),
        tag: 'saturday',
        icon: <CalendarDayRegular />,
    },
    {
        from: generateFromDate('sunday'),
        to: generateToDate('sunday'),
        tag: 'sunday',
        icon: <CalendarDayRegular />,
    },
]

export default function BaseIntervalFilterMenu(props: BaseIntervalOverviewFilterProps) {
    const { values, setValues } = useFormikContext<BaseIntervalOverviewFilterType>()
    const overviewStyles = useOverviewStyles()
    const { t: translate } = useTranslation()

    const checkedValues = useMemo<Record<string, string[]>>(
        () => ({
            tag: values.tag ? [`${values.tag}`] : [],
        }),
        [values.tag],
    )

    //TODO add deps to trigger rerender of day tags.
    const sortedFilterTagDayItems: BaseIntervalOverviewFilterType[] = useMemo(
        () => filterTagDayItems.filter(Boolean).sort((a, b) => a.from.localeCompare(b.from)),
        [],
    )

    return (
        <Menu positioning={{ autoSize: true }} checkedValues={checkedValues}>
            <MenuTrigger disableButtonEnhancement>
                <Tooltip withArrow relationship={'label'} content={translate('base.filter.interval.tooltip')}>
                    <Button
                        appearance={'secondary'}
                        className={mergeClasses(overviewStyles.filterTagButton, values.tag && overviewStyles.filterTagButtonSelected)}
                        icon={!values.tag ? <CalendarRegular /> : undefined}
                    >
                        {values.tag && translate(`base.filter.interval.${values.tag}`)}
                    </Button>
                </Tooltip>
            </MenuTrigger>

            <MenuPopover>
                <MenuList>
                    {sortedFilterTagDayItems?.map((tag, index) => (
                        <MenuItemRadio
                            key={index}
                            name={'tag'}
                            value={`${tag.tag}`}
                            onClick={() => {
                                setValues({
                                    ...values,
                                    ...tag,
                                })
                            }}
                        >
                            {translate(tag.title ? tag.title : `base.filter.interval.${tag.tag}`)}
                        </MenuItemRadio>
                    ))}
                    {filterTagDayItems?.length > 0 && <Divider />}
                    {props.filterItems?.map((tag, index) => (
                        <MenuItemRadio
                            key={index}
                            name={'tag'}
                            value={`${tag.tag}`}
                            onClick={() => {
                                setValues({
                                    ...values,
                                    ...tag,
                                })
                            }}
                        >
                            {translate(tag.title ? tag.title : `base.filter.interval.${tag.tag}`)}
                        </MenuItemRadio>
                    ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    )
}
